import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import Logo from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/products", label: "Products" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
  { to: "/legal", label: "Legal" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const cta = user ? "Dashboard" : "Login";

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled ? "border-slate-200 bg-white/85 backdrop-blur-xl" : "border-transparent bg-white"
      }`}
      data-testid="marketing-navbar"
    >
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo-link"><Logo size={26} /></Link>

        <div className="hidden items-center gap-6 md:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors hover:text-pulse-navy ${
                location.pathname === l.to ? "text-pulse-navy" : "text-slate"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        {/* Right: Login/Dashboard always visible + mobile menu toggle */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(user ? "/dashboard" : "/login")}
            data-testid="nav-login-btn"
            className="rounded-md bg-pulse-navy px-4 py-2 text-sm font-semibold text-white transition-transform duration-150 hover:bg-[#0d2444] active:scale-[0.97] sm:px-5 sm:py-2.5"
          >
            {cta}
          </button>
          <button className="grid h-9 w-9 place-items-center rounded-md text-pulse-navy transition-colors hover:bg-cloud md:hidden"
            onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="border-t border-slate-200 bg-white px-5 py-3 md:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                className={`flex items-center justify-between rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                  location.pathname === l.to ? "bg-cloud text-pulse-navy" : "text-charcoal hover:bg-cloud"
                }`}>
                {l.label}
                <ArrowRight size={15} className="text-slate" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
